import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { useCourse } from '../../hooks/useApi'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { Clock, FileText, Lock } from 'lucide-react'
import { Icon } from '@iconify/react'
import type { ModuleInfo, LessonWithProgress } from '../../types/api'
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen'
import CourseLanding from './CourseLanding'
import './CourseDetailPage.css'

const CourseDetailPage = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const courseIdNumber = courseId ? parseInt(courseId, 10) : null
  const { data, isLoading, error } = useCourse(courseIdNumber)

  useTelegramBackButton(true)

  const handleLessonClick = (lessonId: number, isLocked: boolean) => {
    if (isLocked) return
    navigate(`/courses/${courseId}/lesson/${lessonId}`)
  }

  const handleBuyClick = (buyLink: string) => {
    window.open(buyLink, '_blank', 'noopener,noreferrer')
  }

  if (!courseId || isNaN(courseIdNumber!)) {
    return <Navigate to="/courses" replace />
  }

  if (isLoading) {
    return <LoadingScreen isLoading={true} />
  }

  if (error) {
    const errorDetail = (error as any)?.response?.data?.detail
    const isAccessDenied = (error as any)?.response?.status === 403

    if (isAccessDenied) {
      return (
        <div className="course-detail-error">
          <h2>Нет доступа</h2>
          <p>{errorDetail || 'У вас нет доступа к этому курсу'}</p>
          <button onClick={() => navigate('/courses')}>К курсам</button>
        </div>
      )
    }

    return (
      <div className="course-detail-error">
        <h2>Ошибка загрузки</h2>
        <p>Не удалось загрузить курс</p>
        <button onClick={() => navigate('/courses')}>К курсам</button>
      </div>
    )
  }

  if (!data?.course) {
    return (
      <div className="course-detail-error">
        <h2>Курс не найден</h2>
        <button onClick={() => navigate('/courses')}>К курсам</button>
      </div>
    )
  }

  const { course } = data
  const hasAccess = course.modules && course.modules.length > 0
  const landing = course.landing

  // Подсчёт общего прогресса
  const totalLessons = course.modules?.reduce(
    (acc: number, m: ModuleInfo) => acc + (m.base.lesson_count || 0),
    0
  ) || 0
  const viewedLessons = course.modules?.reduce(
    (acc: number, m: ModuleInfo) => acc + (m.progress?.views || 0),
    0
  ) || 0
  const progressPercent = totalLessons > 0 ? (viewedLessons / totalLessons) * 100 : 0

  // Первое изображение курса
  const courseImage = course.base.header_images?.[0] || course.base.icon_url

  return (
    <div className="course-detail-page safe-area-inset">
      {/* Landing - показываем полноэкранно если нет доступа и есть лендинг */}
      {!hasAccess && landing && (
        <CourseLanding buyLink={landing.buy_link} onBuyClick={handleBuyClick} />
      )}

      <div className="course-detail-content">
        {/* Заголовок, изображение, мета, описание — только без лендинга */}
        {(hasAccess || !landing) && (
          <>
            <h1 className="course-detail-title">{course.base.name}</h1>

            {courseImage && (
              <div className="course-detail-image-wrapper">
                <img src={courseImage} alt={course.base.name} className="course-detail-image" />
              </div>
            )}

            <div className="course-detail-meta">
              {course.base.duration_hours && (
                <div className="course-meta-item">
                  <Clock size={24} />
                  <span>~ {course.base.duration_hours} часов</span>
                </div>
              )}
              <div className="course-meta-item">
                <Icon icon="hugeicons:task-01" width={24} height={24} />
                <span>{totalLessons} уроков</span>
              </div>
            </div>

            {hasAccess && totalLessons > 0 && (
              <div className="course-detail-progress">
                <div className="course-detail-progress-bar">
                  <div
                    className="course-detail-progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="course-detail-progress-info">
                  <span className="course-detail-progress-label">Пройдено</span>
                  <span className="course-detail-progress-count">{viewedLessons}/{totalLessons}</span>
                </div>
              </div>
            )}

            {course.base.description && (
              <p className="course-detail-description">{course.base.description}</p>
            )}
          </>
        )}

        {/* Modules - показываем если есть доступ */}
        {hasAccess && (
          <div className="course-modules">
            {course.modules.map((module: ModuleInfo, moduleIndex: number) => {
              const moduleViews = module.progress?.views || 0
              const moduleLessons = module.base.lesson_count || 0
              const isModuleLocked = module.lessons?.every(l => l.base.is_locked) ?? false

              return (
                <div key={module.base.module_id} className="module-section">
                  <div className="module-header">
                    <div className="module-header-left">
                      {isModuleLocked && (
                        <Lock size={18} className="module-lock-icon" />
                      )}
                      <h3 className="module-title">
                        Модуль {moduleIndex + 1}. {module.base.name}
                      </h3>
                    </div>
                    <span className="module-progress">{moduleViews}/{moduleLessons}</span>
                  </div>

                  {module.lessons && module.lessons.length > 0 && (
                    <div className="module-lessons-scroll">
                      {module.lessons.map((lesson: LessonWithProgress, lessonIndex: number) => (
                        <div
                          key={lesson.base.lesson_id}
                          className={`lesson-card ${lesson.base.is_locked ? 'locked' : ''} ${lesson.is_viewed ? 'viewed' : ''}`}
                          onClick={() => handleLessonClick(lesson.base.lesson_id, lesson.base.is_locked)}
                        >
                          <div className="lesson-card-image-wrapper">
                            {lesson.card.icon_url ? (
                              <img
                                src={lesson.card.icon_url}
                                alt=""
                                className="lesson-card-image"
                              />
                            ) : (
                              <div className="lesson-card-placeholder">
                                <FileText size={24} />
                              </div>
                            )}
                            {/* Статус иконка */}
                            {lesson.is_viewed && !lesson.base.is_locked && (
                              <div className="lesson-status-icon viewed">
                                <Icon icon="iconamoon:check-bold" width={28} height={28} color="#FFFFFF" />
                              </div>
                            )}
                            {lesson.base.is_locked && (
                              <div className="lesson-status-icon locked">
                                <Lock size={14} />
                              </div>
                            )}
                          </div>
                          <div className="lesson-card-info">
                            <span className="lesson-card-number">Урок {lessonIndex + 1}</span>
                            <span className="lesson-card-title">{lesson.card.title}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Нет доступа и нет лендинга */}
        {!hasAccess && !landing && (
          <div className="course-no-access">
            <Icon icon="solar:lock-linear" width={40} height={40} />
            <p>У вас нет доступа к этому курсу</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default CourseDetailPage
